import { useEffect, useState, useCallback, useContext, memo, useMemo } from 'react'
import {
  BBox,
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Polygon,
  GeoJsonProperties,
} from 'geojson'
import { MapContext } from './mapContext'
import GeoJSON from './geoJson'
import { FeatureCollectionWithOptionalInfoAndStyle } from '~/components/mapContent/utils/geoAndStatsToFeatureCollection'
import { h3ResolutionResolveByZoom } from '~/prix/utils/h3ResolutionResolve'
import { Marker } from 'mapbox-gl'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import { NeoClusterPosition } from '../map/mapPosition'
import { useStyles } from '~/components/mapContent/utils/hooks/useStylesForLegends'
import { debounce } from 'debounce'
import bbox from '@turf/bbox'
import bboxPolygon from '@turf/bbox-polygon'
import transformScale from '@turf/transform-scale'
import booleanIntersects from '@turf/boolean-intersects'
import { getHexagonSizeOnScreen } from '../map/hexagon'
import { formatNumberAsCluster } from '../map/formatNumberAsCluster'
import { ActionCall } from '~/prix/react/hooks/concurrentActions'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import centerOfMass from '@turf/center-of-mass'
import { GenericObject } from '~/prix/types/type'
import {
  calculateH3Groups,
  callActionLoadHexagon,
  createHexagonFeature,
  getInitialMaxResolution,
  getMaxResolution,
  getRange,
  getUnitFormat,
  handleClickHexagon,
  NeoClusterClickReturn,
} from '../map/utils/neoCluster.utils'
import { ClusterProps } from '../map/neoCluster'

const NeoCluster = ({
  positions,
  layers,
  layerIndex,
  layerRequest,
  delimitation,
  onClickCluster,
  onChangeLegend,
}: ClusterProps) => {
  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Cluster must be used within a MapBox map')
  }
  const { map } = mapContext
  const [queryFeature, setQueryFeature] = useState<Feature<Polygon | MultiPolygon, GenericObject> | null>(null)
  const [isVisibleLabels, setIsVisibleLabels] = useState(false)
  const [currentResolution, setCurrentResolution] = useState<number>(6)
  const [h3Groups, setH3Groups] = useState<Record<string, NeoClusterPosition[]>>({})
  const [hexagonFeatures, setHexagonFeatures] = useState<
    FeatureCollection<Polygon | MultiPolygon, GenericObject>
  >({
    type: 'FeatureCollection',
    features: [],
  })

  const INITIAL_MAX_RESOLUTION = getInitialMaxResolution(layers[layerIndex].space.delimitationType)
  const MAX_RESOLUTION = getMaxResolution(positions)

  const unitFormat = getUnitFormat(layers[layerIndex].variable)

  const ranges = useMemo(() => {
    const range = getRange(h3Groups, unitFormat)

    if (onChangeLegend) debounce(() => onChangeLegend(layerIndex, range), 200)()

    return layers.map((layer, index) => (index === layerIndex ? range : { min: 0, max: 0 }))
  }, [layers, h3Groups])

  const { hexagonStyles } = useStyles({ layers, ranges })

  useEffect(() => {
    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    const layers = map.getStyle().layers
    const previousVisibility: Record<string, 'visible' | 'none'> = {}

    for (const layer of layers) {
      if (
        layer.type === 'symbol' &&
        (layer.id.includes('label') ||
          layer.id.includes('place') ||
          layer.id.includes('highway') ||
          layer.id.includes('road') ||
          layer.id.includes('street'))
      ) {
        previousVisibility[layer.id] =
          map.getLayoutProperty(layer.id, 'visibility') === 'none' ? 'none' : 'visible'
        map.setLayoutProperty(layer.id, 'visibility', 'none')
      }
    }

    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }

      for (const layer of layers) {
        if (
          layer.type === 'symbol' &&
          (layer.id.includes('label') ||
            layer.id.includes('place') ||
            layer.id.includes('highway') ||
            layer.id.includes('road') ||
            layer.id.includes('street'))
        ) {
          map.setLayoutProperty(layer.id, 'visibility', previousVisibility[layer.id])
        }
      }
    }
  }, [map])

  const updateHexagons = useCallback(() => {
    if (!map) return

    const style = hexagonStyles[layerIndex]

    const features = createHexagonFeature(
      h3Groups,
      unitFormat,
      style,
      delimitation,
      renderVisibleHexagons,
      currentResolution,
      INITIAL_MAX_RESOLUTION,
    )

    handleShowNumbers()

    setHexagonFeatures({
      type: 'FeatureCollection',
      features,
    })
  }, [h3Groups, currentResolution])

  const renderVisibleHexagons = useCallback(
    (feature: Geometry | Feature<any, GenericObject>) => {
      if (!map) return

      const bounds = map.getBounds()
      if (!bounds) return false
      const viewportBBox = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ] as BBox

      const viewportPolygon = bboxPolygon(viewportBBox)
      const expandedViewportPolygon = transformScale(viewportPolygon, 1.2)

      return booleanIntersects(expandedViewportPolygon, feature)
    },
    [map],
  )

  const updateH3Groups = useCallback(() => {
    const h3Groups = calculateH3Groups(positions, currentResolution)

    setH3Groups(h3Groups)
    return h3Groups
  }, [positions, currentResolution])

  const updateLabelMarkers = () => {
    if (!map || !isVisibleLabels) return

    return hexagonFeatures.features.map(feature => {
      if (feature.properties!.isRemainder === true) {
        return null
      }

      const center = centerOfMass(feature).geometry.coordinates
      const element = document.createElement('div')
      element.className = 'marker'
      element.style.width = `60px`
      element.style.height = `60px`
      element.style.backgroundSize = '100%'
      element.style.display = 'flex'
      element.style.alignItems = 'center'
      element.style.textAlign = 'center'
      element.style.justifyContent = 'center'
      element.style.fontSize = '16px'
      element.style.fontWeight = 'bold'
      element.style.color = 'white'
      element.style.filter = 'drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.5))'
      element.innerHTML = feature.properties!.isLoading
        ? 'Carregando...'
        : String(formatNumberAsCluster(Number(feature.properties!.value ?? 0))) +
          (unitFormat === 'percentage' ? '%' : '') +
          (feature.properties?.customLegend ? `<br/> ${feature.properties?.customLegend}` : '')

      const marker = new Marker(element).setLngLat([center[0], center[1]]).addTo(map)
      return marker
    })
  }

  const handleShowNumbers = useCallback(() => {
    if (!map) return

    const center = map.getCenter()
    const currentZoom = map.getZoom()

    const size = getHexagonSizeOnScreen(currentResolution, currentZoom, center.lat)
    setIsVisibleLabels(size >= 15)
  }, [map, currentResolution])

  useEffect(() => {
    if (!map || !isVisibleLabels) return

    const markers = updateLabelMarkers()

    return () => {
      markers?.map((marker: any) => marker?.remove())
    }
  }, [map, hexagonFeatures, isVisibleLabels])

  useEffect(() => {
    updateH3Groups()
  }, [positions, currentResolution])

  useEffect(() => {
    updateHexagons()
  }, [h3Groups])

  useEffect(() => {
    if (!map) return
    updateH3Groups()
  }, [map])

  useEffect(() => {
    if (!map) return

    const handleMoveEnd = debounce(() => {
      if (!map) return

      const currentZoom = map.getZoom()
      const newResolution = h3ResolutionResolveByZoom(currentZoom, undefined, MAX_RESOLUTION)

      if (newResolution === currentResolution) {
        updateHexagons()
        return
      }

      setCurrentResolution(newResolution)
    }, 100)

    const handleZoomStart = () => {
      setIsVisibleLabels(false)
    }

    map.on('moveend', handleMoveEnd)
    map.on('zoomstart', handleZoomStart)
    return () => {
      map.off('moveend', handleMoveEnd)
      map.off('zoomstart', handleZoomStart)
    }
  }, [map, currentResolution, h3Groups])

  const handleClick = useCallback(
    (event: mapboxgl.MapMouseEvent, feature: Feature) => {
      event.preventDefault()
      if (!map) return

      const h3 = feature.properties!.h3
      const currentZoom = map.getZoom()

      const hexFeature = hexagonFeatures.features.find((f: any) => f.properties!.h3 === h3)
      if (!hexFeature) {
        return
      }

      const actionClick = handleClickHexagon(
        currentZoom,
        MAX_RESOLUTION,
        h3,
        hexFeature,
        hexagonFeatures,
      ) as NeoClusterClickReturn

      switch (actionClick) {
        case NeoClusterClickReturn.hexagonNotFound:
          return

        case NeoClusterClickReturn.queryFeature:
          setQueryFeature(hexFeature)
          break

        case NeoClusterClickReturn.maxZoom:
          if (onClickCluster) {
            onClickCluster(
              hexFeature as Feature<
                Polygon | MultiPolygon,
                {
                  h3: string
                  value: number
                  preservedAreaPercentage: number
                }
              >,
            )
          }
          break

        default:
        case NeoClusterClickReturn.zoomToHexagon:
          zoomToHexagon(feature)
          break
      }
    },
    [hexagonFeatures, onClickCluster],
  )

  const { callAction } = useActionFunction(layerRequest.actionDefinition)

  const showH3Loading = useCallback(
    (index: number) => {
      setHexagonFeatures((prevData: any) => {
        prevData.features[index].properties!.fillColor = 'rgba(0,0,0,0.8)'
        prevData.features[index].properties!.strokeColor = 'rgba(0,0,0,0.8)'
        prevData.features[index].properties!.isLoading = true
        return { ...prevData }
      })
    },
    [map, hexagonFeatures],
  )

  useEffect(() => {
    if (!queryFeature || !layerRequest) return

    getResolutionFeatures(queryFeature)
  }, [layerRequest, queryFeature])

  const getResolutionFeatures = useCallback(
    (feature: Feature<Polygon | MultiPolygon>) => {
      callActionLoadHexagon(
        feature,
        positions,
        layerRequest as any,
        hexagonFeatures,
        callAction,
        showH3Loading,
        updateH3Groups,
        zoomToHexagon,
      )
    },
    [layerRequest, positions, hexagonFeatures],
  )

  const zoomToHexagon = useCallback(
    (feature: Feature) => {
      if (!map) return

      const bounds = bbox(feature.geometry as GeoJSON.Polygon)

      map.fitBounds(bounds as mapboxgl.LngLatBoundsLike, {
        linear: true,
        padding: 0,
      })
    },
    [map],
  )

  return (
    <GeoJSON
      geoJson={hexagonFeatures as FeatureCollectionWithOptionalInfoAndStyle}
      onClick={handleClick}
    />
  )
}

export default memo(NeoCluster)
